'use client';
import './productLibrary.scss';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ProviderImg from '@/assets/img/provider.png';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid2,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { triggerEvent } from '@/lib/events';
import { useSpinnerContext } from '@/context/SpinnerContext';
import { clamp, makeResolutionScale } from '@/utils/resolutionScale';
import {
  arrayToCommaDelimatedString,
  capitalizeFirstLetter,
  decodePLSearchParams,
  productLibraryFiltersChipsFromUrlParamsData,
  productLibrarySearchParamsUrl,
  providersFilterLogic,
  providersSortByLogic,
} from '@/lib/utils';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AES } from 'crypto-js';
import { productLibraryDefaultFilterValues, productLibrarySortByOptions } from '@/constants';
import { useTranslation } from 'react-i18next';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEventHub } from '@/services/eventEnums';
import { useDebouncedCallback } from 'use-debounce';
import { OrganizedProviderData } from '@/app/[locale]/product-library/interfaces';
import { FilterOption, Filters, ProviderProps } from './interfaces';

export default function ProductLibrary({
  providersPropData,
  secretKey,
  resolutionRange,
  optionsData,
}: ProviderProps) {
  const { stopLoaderEvent, startLoaderEvent } = useSpinnerContext();
  const { t } = useTranslation('provider');
  const searchedText = useRef<any>(null);
  const filtersBasedData = useRef([]);
  const [sortBy, setSortBy] = useState<string>();

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [filters, setFilters] = useState<Filters>(productLibraryDefaultFilterValues);
  const [showFilterDrawer, setShowFilterDrawer] = useState<boolean>(false);
  // const [urlSearchParams,setUrlSearchParams] = useState()
  const [chips, setChips] = useState<string[]>([]);
  const [providersData, setProvidersData] = useState<OrganizedProviderData[]>();
  const [resolutionRangeData, setResolutionRangeData] = useState<any>([
    resolutionRange?.min,
    resolutionRange?.max,
  ]);

  // Text drafts for the Min/Max inputs. Typing only updates these, so in-progress text
  // like "20." (or a cleared field) is preserved; the value is parsed, clamped, and
  // applied on blur/Enter, then the drafts re-sync from the committed range below.
  const [minDraft, setMinDraft] = useState<string>(String(resolutionRange?.min ?? 0));
  const [maxDraft, setMaxDraft] = useState<string>(String(resolutionRange?.max ?? 0));

  // Re-sync the drafts whenever the committed range changes elsewhere.
  useEffect(() => {
    setMinDraft(String(resolutionRangeData[0]));
    setMaxDraft(String(resolutionRangeData[1]));
  }, [resolutionRangeData]);

  // Weighted scale reused from the archive slider, parameterised to this brochure's
  // dynamic min/max. Breakpoint 3m matches the archive's dense-low-end weighting and
  // degrades to a linear scale when the product range doesn't straddle 3m.
  const resolutionScale = useMemo(
    () =>
      makeResolutionScale({
        min: resolutionRange?.min ?? 0,
        max: resolutionRange?.max ?? 0,
        breakpoint: 3,
        breakpointPct: 85,
      }),
    [resolutionRange?.min, resolutionRange?.max]
  );

  // Marks for the weighted track. Labelled: range endpoints, fine-zone references, and
  // the 3m breakpoint (the main divider). Plus a single unlabelled tick at 20m in the
  // compressed coarse zone leading toward the max — kept label-less so its text can't
  // collide in the tight 3→max band.
  const resolutionMarks = useMemo(() => {
    const min = resolutionRange?.min ?? 0;
    const max = resolutionRange?.max ?? 0;
    const labelled = Array.from(new Set([min, 1, 2, 3, max]))
      .filter(v => v >= min && v <= max)
      .sort((a, b) => a - b);
    const coarseTicks = [20].filter(v => v > 3 && v < max && !labelled.includes(v));
    return [
      ...labelled.map(metres => ({
        value: resolutionScale.toPosition(metres),
        label: `${metres}`,
      })),
      ...coarseTicks.map(metres => ({ value: resolutionScale.toPosition(metres) })),
    ];
  }, [resolutionRange?.min, resolutionRange?.max, resolutionScale]);

  useEffect(() => {
    const decodedUrl = decodePLSearchParams(searchParams.toString());
    const decodedUrlFilters = decodedUrl.filters;
    const decodedSearchText = decodedUrl.search;
    const newFiltersData = { ...filters, ...decodedUrlFilters };

    const decodedFiltersChips = productLibraryFiltersChipsFromUrlParamsData(decodedUrlFilters);

    let data: any = [];

    data = providersFilterLogic(providersPropData, newFiltersData, sortBy);
    filtersBasedData.current = data;
    if (decodedSearchText) {
      data = filterProductsOnSearch(decodedSearchText, data);
      searchedText.current = decodedSearchText;
    }
    if (newFiltersData?.resolutionRange && newFiltersData?.resolutionRange?.length > 0) {
      setResolutionRangeData(newFiltersData?.resolutionRange);
    }

    setProvidersData(data);
    setFilters(newFiltersData);
    setChips(decodedFiltersChips);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providersPropData]);

  useEffect(() => {
    triggerEvent(stopLoaderEvent);
    let data: any = [];

    data = providersFilterLogic(providersPropData, filters, sortBy);
    filtersBasedData.current = data;
    if (searchedText.current) {
      data = filterProductsOnSearch(searchedText.current, data);
    }
    setProvidersData(data);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, stopLoaderEvent]);

  const router = useRouter();

  const handleDrawerClose = (): void => {
    setShowFilterDrawer(false);
  };

  const handleSearchMixpanelEvent = (query: string): void => {
    MixpanelTracking.getInstance().track(MixpanelEventHub.PROVIDER_SEARCH, {
      search_string: query,
    });
  };

  const handleSearchMixpanelEventDebounced = useDebouncedCallback(handleSearchMixpanelEvent, 400);

  const filterProductsOnSearch = (query: any, providersData: any) => {
    const searchedCards = providersData?.filter((item: any) => {
      const regexPattern = `${query}`;
      const regex = new RegExp(regexPattern, 'i');
      return item?.partner_name?.match(regex) || item?.product_name?.match(regex);
    });

    const results = searchedCards?.filter(
      (obj: any, index: number, self: any) =>
        index === self.findIndex((t: any) => t?.product_id === obj?.product_id)
    );
    return results;
  };
  const handleSearch = (query: string): void => {
    handleSearchMixpanelEventDebounced(query);
    searchedText.current = query;
    const dataForSearchParams = { ...filters, search: query };
    const searchParamsUrl = productLibrarySearchParamsUrl(dataForSearchParams);
    window.history.pushState(null, '', pathname + '?' + searchParamsUrl);

    const results = filterProductsOnSearch(query, filtersBasedData.current);
    setProvidersData(results);
  };

  // Commits the resolution range to the URL, filters, and analytics — debounced so
  // dragging or typing doesn't spam browser history + Mixpanel or re-filter the product
  // list on every event. Only the last value after a 400ms pause is written.
  const commitResolutionRange = useDebouncedCallback((resolutionValue: [number, number]) => {
    const updatedFilters = { ...filters, resolutionRange: resolutionValue };
    const dataForSearchParams = { ...updatedFilters, search: searchedText.current };
    const searchParamsUrl = productLibrarySearchParamsUrl(dataForSearchParams);
    window.history.pushState(null, '', pathname + '?' + searchParamsUrl);
    setFilters(prev => ({ ...prev, resolutionRange: resolutionValue }));
    MixpanelTracking.getInstance().track(MixpanelEventHub.PROVIDER_FILTER_BY_CLICK, {
      resolution_min: resolutionValue[0],
      resolution_max: resolutionValue[1],
      order_types: filters?.orderType,
      providers: filters?.provider,
      sensor_types: filters?.sensorType,
      product_types: filters?.productType,
    });
  }, 400);

  // Single writer for the resolution filter. Updates the slider/Min/Max immediately for a
  // responsive UI; the URL, filters, and analytics are committed on a debounce.
  const applyResolutionRange = (resolutionValue: [number, number]) => {
    setResolutionRangeData(resolutionValue);
    commitResolutionRange(resolutionValue);
  };

  // Parse + clamp + apply the Min draft on blur/Enter. Blank or non-numeric text reverts
  // to the current committed value rather than committing a bogus range.
  const commitResolutionMin = () => {
    const parsed = Number(minDraft);
    if (minDraft.trim() === '' || Number.isNaN(parsed)) {
      setMinDraft(String(resolutionRangeData[0]));
      return;
    }
    const min = clamp(parsed, resolutionRange?.min ?? 0, resolutionRangeData[1]);
    applyResolutionRange([min, resolutionRangeData[1]]);
  };

  const commitResolutionMax = () => {
    const parsed = Number(maxDraft);
    if (maxDraft.trim() === '' || Number.isNaN(parsed)) {
      setMaxDraft(String(resolutionRangeData[1]));
      return;
    }
    const max = clamp(parsed, resolutionRangeData[0], resolutionRange?.max ?? 0);
    applyResolutionRange([resolutionRangeData[0], max]);
  };

  return (
    <>
      <Box className="provider-header">
        <Typography variant="h4">{t('providerTitle')}</Typography>
        <Box sx={{ width: '100%' }}>
          <Box className="provider-search-wrapper">
            <IconButton
              onClick={() => setShowFilterDrawer(!showFilterDrawer)}
              size="small"
              className="btn-filter"
              aria-label="filter"
              color="secondary"
            >
              <FilterAltIcon />
            </IconButton>
            <TextField
              className="skywatch-search"
              variant="filled"
              size="small"
              value={searchedText.current}
              placeholder={t('common:search')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setProvidersData(providersPropData);
                handleSearch(e.target.value);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <PopupState variant="popover" popupId="demo-popup-menu">
              {popupState => (
                <>
                  <Button
                    name="sort-by-button"
                    aria-label="sort-by-button"
                    color="secondary"
                    startIcon={<SortIcon />}
                    endIcon={<ExpandMoreIcon />}
                    variant="outlined"
                    {...bindTrigger(popupState)}
                  >
                    {t('sortBy')}
                  </Button>
                  <Menu
                    PaperProps={{
                      className: 'sort-dropdown',
                    }}
                    {...bindMenu(popupState)}
                  >
                    <FormControl>
                      <FormLabel id="demo-radio-buttons-group-label">{t('sortByTitle')}</FormLabel>
                      <RadioGroup
                        value={sortBy}
                        // aria-labelledby="demo-radio-buttons-group-label"
                        aria-label="sort-by-radio-group"
                        name="radio-buttons-group"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          popupState.close();
                          const value = e.target.value;
                          setSortBy(value);
                          const sortedData = providersSortByLogic(providersData, value);
                          setProvidersData(sortedData);
                          MixpanelTracking.getInstance().track(
                            MixpanelEventHub.PROVIDER_SORT_BY_CLICK,
                            { sort_by: value }
                          );
                        }}
                      >
                        {productLibrarySortByOptions?.map((item: string, index: number) => (
                          <FormControlLabel
                            key={index}
                            value={item}
                            control={<Radio size="small" color="secondary" />}
                            label={t(item)}
                            aria-label={`sort-by-radio-${item}`}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Menu>
                </>
              )}
            </PopupState>
          </Box>
          {chips?.length > 0 && (
            <Box className="filter-chips" aria-label="filter-chips-container">
              {chips?.map((item: string) => (
                <Chip
                  size="small"
                  key={item}
                  color="primary"
                  label={capitalizeFirstLetter(item)}
                  variant="outlined"
                />
              ))}
              <Link
                aria-label="clear-all-filters-link"
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setChips([]);
                  setFilters(productLibraryDefaultFilterValues);
                }}
              >
                {t('clearAll')}
              </Link>
            </Box>
          )}
        </Box>
      </Box>
      <Box className="provider-content-wrapper">
        <Box className={`provider-filters ${showFilterDrawer ? 'show-filter-drawer' : ''}`}>
          <Typography component="div" variant="h6">
            {t('filterBy')}
          </Typography>
          <Box className="provider-filters-content">
            <Box className="filter-content">
              <Typography variant="subtitle1">{t('resolution')}</Typography>
              <Slider
                color="primary"
                sx={{
                  '& .MuiSlider-markLabel': { color: 'text.primary' },
                  '& .MuiSlider-mark, & .MuiSlider-markActive': {
                    height: 10,
                    width: 2,
                    opacity: 1,
                    backgroundColor: 'text.primary',
                  },
                }}
                slotProps={{
                  input: {
                    'aria-label': 'resolution-slider-input',
                  },
                }}
                name="resolution-slider"
                valueLabelDisplay="auto"
                valueLabelFormat={(position: number) => `${resolutionScale.toValue(position)}m`}
                marks={resolutionMarks}
                min={0}
                max={100}
                step={0.1}
                value={resolutionRangeData.map(resolutionScale.toPosition)}
                onChange={(_, value: number | number[]) => {
                  const resolutionValue = (value as number[]).map(resolutionScale.toValue) as [
                    number,
                    number,
                  ];
                  applyResolutionRange(resolutionValue);
                }}
              />
              <Stack direction="row" spacing={1}>
                <Box className="resulution-content">
                  <Typography variant="caption" display="block">
                    {t('minimum')}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    type="text"
                    value={minDraft}
                    onChange={e => setMinDraft(e.target.value)}
                    onBlur={commitResolutionMin}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitResolutionMin();
                    }}
                    slotProps={{
                      htmlInput: {
                        inputMode: 'decimal',
                        'aria-label': t('minimum'),
                      },
                    }}
                    sx={{
                      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                        borderBottom: 'none',
                      },
                      '& .MuiInput-underline:hover:before': { borderBottom: 'none' },
                      '& input': { padding: 0 },
                    }}
                  />
                </Box>
                <Box className="resulution-content">
                  <Typography variant="caption" display="block">
                    {t('maximum')}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    type="text"
                    value={maxDraft}
                    onChange={e => setMaxDraft(e.target.value)}
                    onBlur={commitResolutionMax}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitResolutionMax();
                    }}
                    slotProps={{
                      htmlInput: {
                        inputMode: 'decimal',
                        'aria-label': t('maximum'),
                      },
                    }}
                    sx={{
                      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                        borderBottom: 'none',
                      },
                      '& .MuiInput-underline:hover:before': { borderBottom: 'none' },
                      '& input': { padding: 0 },
                    }}
                  />
                </Box>
              </Stack>
            </Box>

            {optionsData &&
              optionsData?.map((item: FilterOption, index: number) => (
                <Box className="filter-content" key={index}>
                  <Typography variant="subtitle1">{t(item?.key)}</Typography>
                  <FormGroup aria-label={`filter-${item?.key}-field-group`}>
                    {item?.options?.length > 0 &&
                      item?.options?.map((childItem: string, childIndex: number) => (
                        <FormControlLabel
                          aria-label={`filter-${item?.key}-${childItem}`}
                          key={childIndex}
                          control={
                            <Checkbox
                              size="small"
                              checked={filters[item?.key as keyof Filters]?.includes(
                                childItem as never
                              )}
                              name={childItem}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newFilter = {
                                  [item.key]: e.target.checked
                                    ? [...filters[item?.key as keyof Filters], childItem]
                                    : filters[item?.key as keyof Filters].filter(
                                        innerChildItem => childItem !== innerChildItem
                                      ),
                                };
                                const updatedFilters = { ...filters, ...newFilter };
                                const dataForSearchParams = {
                                  ...updatedFilters,
                                  search: searchedText.current,
                                };
                                const searchParamsUrl =
                                  productLibrarySearchParamsUrl(dataForSearchParams);
                                window.history.pushState(
                                  null,
                                  '',
                                  pathname + '?' + searchParamsUrl
                                );
                                setFilters(updatedFilters);
                                setChips(prev =>
                                  prev.includes(childItem)
                                    ? prev.filter(innerChildItem => childItem !== innerChildItem)
                                    : [...prev, childItem]
                                );
                                MixpanelTracking.getInstance().track(
                                  MixpanelEventHub.PROVIDER_FILTER_BY_CLICK,
                                  {
                                    resolution_min: updatedFilters?.resolutionRange[0],
                                    resolution_max: updatedFilters?.resolutionRange[1],
                                    order_types: updatedFilters?.orderType,
                                    providers: updatedFilters?.provider,
                                    sensor_types: updatedFilters?.sensorType,
                                    product_types: updatedFilters?.productType,
                                  }
                                );
                              }}
                            />
                          }
                          label={capitalizeFirstLetter(childItem)}
                        />
                      ))}
                  </FormGroup>
                </Box>
              ))}
          </Box>
        </Box>

        {/* Cards */}
        <Box className="provider-card-wrapper">
          <Grid2 container spacing={2}>
            {providersData && providersData?.length > 0 ? (
              providersData?.map((item: OrganizedProviderData, index: number) => {
                const bands: string =
                  item?.bands?.length > 0 ? arrayToCommaDelimatedString(item?.bands) : '';
                const categoryList: string =
                  item?.category?.length > 0
                    ? arrayToCommaDelimatedString(item?.category, true)
                    : '';
                return (
                  <Grid2 size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
                    <Card className="provider-card">
                      <CardMedia
                        sx={{ height: 177 }}
                        image={item?.properties?.sample_preview ?? ProviderImg.src}
                      />
                      <CardContent>
                        <Typography
                          gutterBottom
                          variant="h6"
                          component="div"
                          aria-label={`product-name-${index}`}
                        >
                          {`${item?.partner_name} - ${item?.product_name}`}
                        </Typography>
                        <ul aria-label={`product-details-list-${index}`}>
                          <li aria-label={`sensor-type-item-${index}`}>
                            <span aria-label={`sensor-type-label-${index}`}>
                              {t('sensorType')} :{' '}
                            </span>
                            {item?.sensor_type}
                          </li>
                          {item?.category?.length && (
                            <li aria-label={`capture-method-item-${index}`}>
                              <span aria-label={`capture-method-label-${index}`}>
                                {t('captureMethod')} :{' '}
                              </span>
                              {categoryList}
                            </li>
                          )}
                          {item?.bands?.length > 0 && (
                            <li aria-label={`available-bands-item-${index}`}>
                              <span aria-label={`available-bands-label-${index}`}>
                                {t('availableBands')} :{' '}
                              </span>
                              {bands}
                            </li>
                          )}
                          <li aria-label={`spatial-resolution-item-${index}`}>
                            <span aria-label={`spatial-resolution-label-${index}`}>
                              {t('spatialResolution')} :{' '}
                            </span>
                            {`${item?.resolution}m`}
                          </li>
                          <li aria-label={`product-type-item-${index}`}>
                            <span aria-label={`product-type-label-${index}`}>
                              {t('productType')} :{' '}
                            </span>
                            {capitalizeFirstLetter(item?.sensor_mode ?? '')}
                          </li>
                        </ul>
                      </CardContent>
                      <CardActions sx={{ mt: 'auto', justifyContent: 'center' }}>
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          aria-label={`product-card-action-area-${index}`}
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => {
                            triggerEvent(startLoaderEvent);
                            const encrypt = AES.encrypt(JSON.stringify(item), secretKey);
                            sessionStorage.setItem('prov_item', encrypt.toString());
                            router.push(`/product-library/${item?.product_id}`);
                            MixpanelTracking.getInstance().track(
                              MixpanelEventHub.PROVIDER_PRODUCT_CLICK,
                              { product_name: `${item?.partner_name} - ${item?.product_name}` }
                            );
                          }}
                        >
                          See Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid2>
                );
              })
            ) : (
              <Box display="flex" justifyContent="center">
                <Typography variant="h6" className="mt-3 ml-3">
                  {t('noResult')}
                </Typography>
              </Box>
            )}
          </Grid2>
        </Box>
      </Box>
      <Box
        className={`mob-filter-bg ${showFilterDrawer ? 'active' : ''}`}
        onClick={handleDrawerClose}
      />
    </>
  );
}
